import React, { Component } from 'react';
import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { Formik } from 'formik';
import Select from 'react-select';
import * as Yup from 'yup';

export class InputForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            vehicleCategoryOptions: [],
            fuelOptions: [],
            fuels: [],
            selectedVehicleCategory: null,
            selectedFuel: null,
            loadingVehicleCategories: true,
            loadingFuels: true,
            weightDisabled: false,
            co2Disabled: false,
            noxDisabled: false,
            rangeDisabled: true
        };

        this.modelSchema = Yup.object({
            weight: Yup.number()
                .typeError('Vekt har en ugyldig verdi')
                .positive('Vekt må være et positivt tall')
                .integer('Vekt må være et heltall')
                .when('fuel', {
                    is: (val) => !this.state.weightDisabled,
                    then: Yup.number().required('Vekt er påkrevd')
                }),
            co2: Yup.number()
                .typeError('CO2 har en ugyldig verdi')
                .positive('CO2 må være et positivt tall')
                .integer('CO2 må være et heltall')
                .when('fuel', {
                    is: (val) => !this.state.co2Disabled,
                    then: Yup.number().required('CO2 er påkrevd')
                }),
            nox: Yup.number()
                .typeError('NOx har en ugyldig verdi')
                .positive('NOx må være et positivt tall')
                .when('fuel', {
                    is: (val) => !this.state.noxDisabled,
                    then: Yup.number().required('NOx er påkrevd')
                }),
            range: Yup.number()
                .typeError('Rekkevidde har en ugyldig verdi')
                .positive('Rekkevidde må være et positivt tall')
                .when('fuel', {
                    is: (val) => !this.state.rangeDisabled,
                    then: Yup.number().required('Rekkevidde er påkrevd')
                })
        });
    }

    componentDidMount() {
        this.getVehicleCategoryOptions();
        this.getFuelOptions();
    }

    setFieldsEnabledOrDisabled = (fuelOption) => {
        const fuel = this.state.fuels.find(x => x.id === fuelOption.value);

        this.setState({
            weightDisabled: !fuel.weight,
            co2Disabled: !fuel.cO2,
            noxDisabled: !fuel.nOx,
            rangeDisabled: !fuel.range
        });
    }

    handleFuelChange = (selectedOption, setFieldValue, setFieldTouched) => {
        const fuel = this.state.fuels.find(x => x.id === selectedOption.value);

        setFieldTouched('weight', false);
        setFieldTouched('co2', false);
        setFieldTouched('nox', false);
        setFieldTouched('range', false);

        setFieldValue('fuel', selectedOption);

        if (!fuel.weight) {
            setFieldValue('weight', '');
        }

        if (!fuel.cO2) {
            setFieldValue('co2', '');
        }

        if (!fuel.nOx) {
            setFieldValue('nox', '');
        }

        if (!fuel.range) {
            setFieldValue('range', '');
        }

        this.setFieldsEnabledOrDisabled(selectedOption);
    }

    handleSubmit = (values) => {
        this.props.onSubmit({
            'vehicleCategory': values.vehicleCategory.value,
            'weight': values.weight,
            'co2': values.co2 !== '' ? values.co2 : 0,
            'nox': values.nox !== '' ? values.nox : 0,
            'fuel': this.state.fuels.find(x => x.id === values.fuel.value).externalId,
            'range': values.range !== '' ? values.range : 0
        });
    }

    async getVehicleCategoryOptions() {
        fetch('api/FilterCriteria/VehicleCategory?useExternalId=true')
            .then(response => { return response.json(); })
            .then(data => {
                this.setState({
                    vehicleCategoryOptions: data,
                    selectedVehicleCategory: data[0],
                    loadingVehicleCategories: false
                });
            });
    }

    async getFuelOptions() {
        fetch('api/FilterCriteria/Fuel')
            .then(response => { return response.json(); })
            .then(data => {
                this.setState({
                    fuelOptions: data,
                    selectedFuel: data[0],
                    loadingFuels: false
                }, () => this.getFuels());
            });
    }

    async getFuels() {
        fetch('api/ManualCalculation/Fuel')
            .then(response => { return response.json(); })
            .then(data => {
                this.setState({
                    fuels: data
                }, () => this.setFieldsEnabledOrDisabled(this.state.selectedFuel));
            });
    }

    render() {
        return (
            <Formik
                enableReinitialize={true}
                validationSchema={this.modelSchema}
                initialValues={{
                    vehicleCategory: this.state.selectedVehicleCategory,
                    fuel: this.state.selectedFuel,
                    weight: '',
                    co2: '',
                    nox: '',
                    range: ''
                }}
                onSubmit={values => this.handleSubmit(values)}>
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    touched,
                    values,
                    errors,
                    setFieldValue,
                    setFieldTouched
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Form.Group as={Row} controlId="VehicleCategory">
                            <Form.Label column md="2" xs="4">Kjøretøygruppe</Form.Label>
                            <Col md="10" xs="8">
                                {this.state.loadingVehicleCategories
                                    ? <Select isLoading={true} />
                                    : <Select
                                        name="vehicleCategory"
                                        options={this.state.vehicleCategoryOptions}
                                        value={values.vehicleCategory}
                                        onChange={selectedOption => setFieldValue('vehicleCategory', selectedOption)}
                                        isSearchable={!isMobile} />
                                }
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="Fuel">
                            <Form.Label column md="2" xs="4">Energikilde</Form.Label>
                            <Col md="10" xs="8">
                                {this.state.loadingFuels
                                    ? <Select isLoading={true} />
                                    : <Select
                                        name="fuel"
                                        options={this.state.fuelOptions}
                                        value={values.fuel}
                                        onChange={selectedOption => this.handleFuelChange(selectedOption, setFieldValue, setFieldTouched)}
                                        isSearchable={!isMobile} />
                                }
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="Weight">
                            <Form.Label column md="2" xs="4">Vekt</Form.Label>
                            <Col md="10" xs="8">
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        name="weight"
                                        value={values.weight}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={!!touched.weight && !!errors.weight} />
                                    <InputGroup.Append>
                                        <InputGroup.Text>kg</InputGroup.Text>
                                    </InputGroup.Append>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.weight}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="CO2">
                            <Form.Label column md="2" xs="4">CO<sub>2</sub></Form.Label>
                            <Col md="10" xs="8">
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        name="co2"
                                        value={values.co2}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={!!touched.co2 && !!errors.co2}
                                        disabled={this.state.co2Disabled} />
                                    <InputGroup.Append>
                                        <InputGroup.Text>g/km</InputGroup.Text>
                                    </InputGroup.Append>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.co2}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="NOx">
                            <Form.Label column md="2" xs="4">NO<sub>x</sub></Form.Label>
                            <Col md="10" xs="8">
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        name="nox"
                                        value={values.nox}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={!!touched.nox && !!errors.nox}
                                        disabled={this.state.noxDisabled} />
                                    <InputGroup.Append>
                                        <InputGroup.Text>mg/km</InputGroup.Text>
                                    </InputGroup.Append>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.nox}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="range">
                            <Form.Label column md="2" xs="4">Rekkevidde</Form.Label>
                            <Col md="10" xs="8">
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        name="range"
                                        value={values.range}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={!!touched.range && !!errors.range}
                                        disabled={this.state.rangeDisabled} />
                                    <InputGroup.Append>
                                        <InputGroup.Text>km</InputGroup.Text>
                                    </InputGroup.Append>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.range}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Col>
                        </Form.Group>
                        <Row>
                            <Col md="2" xs="4" />
                            <Col md="10" xs="8">
                                <Button type="submit" disabled={this.props.loading}>Beregn</Button>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Formik>    
        );
    }
}